import {
  ATTR_NAMES,
  getAttrFromProduct,
  getAttrFromProductByName,
} from '../types/attrNames';
import { CmsProduct } from '../types/cmsProduct';
import { formatAttributeItemValue } from './helpers/attrHelpers';

/**
 * TI provider page (travel insurance) product-attribute accessors — CMS-1643 /
 * CMS-1644. Read attributes through the typed `getAttrFromProduct` helper; do
 * not hand-write `attributes.find()` with string-literal relation names.
 */

export type FulfilmentPath = 'e2e' | 'click_out';

/**
 * Reads the product's `FULFILMENT_PATH` attribute, tolerantly parsed
 * (trim + lowercase). Anything other than `e2e` / `click_out` (including an
 * absent attribute) resolves to `undefined` — treated as "no path", so no
 * supporting wording is shown while the CTA still works (CMS-1643 AC-16).
 */
export function getFulfilmentPath(
  product?: CmsProduct,
): FulfilmentPath | undefined {
  const normalized = getAttrFromProduct(ATTR_NAMES.FULFILMENT_PATH, product)
    ?.value?.trim()
    .toLowerCase();
  return normalized === 'e2e' || normalized === 'click_out'
    ? normalized
    : undefined;
}

export type PolicyDocuments = {
  policyWording?: string;
  brochure?: string;
};

/**
 * Reads the product's policy document links (Policy Wording + Brochure).
 * Empty / whitespace-only URLs are omitted, so an entry is present only when it
 * has a usable link (CMS-1644 AC-9).
 */
export function getPolicyDocuments(product?: CmsProduct): PolicyDocuments {
  const policyWording = getAttrFromProduct(
    ATTR_NAMES.POLICY_WORDING_DOCUMENT_LINK,
    product,
  )?.value?.trim();
  const brochure = getAttrFromProduct(
    ATTR_NAMES.BROCHURE_DOCUMENT_LINK,
    product,
  )?.value?.trim();

  return {
    ...(policyWording ? { policyWording } : {}),
    ...(brochure ? { brochure } : {}),
  };
}

/**
 * Returns the display string for a single TI eligibility attribute, resolved by
 * its (editor-chosen, so runtime-dynamic) relation name:
 * - plain-text attributes are rendered as entered,
 * - number attributes (`MIN_AGE` / `MAX_AGE`) are number-formatted with their
 *   prefix / suffix,
 * - an absent attribute or an empty value resolves to `undefined`, so the row
 *   auto-hides (no "—" placeholder — CMS-1644 §6.3 / AC-6).
 *
 * Uses `getAttrFromProductByName` (not `getAttrFromProduct`) because eligibility
 * rows can reference any catalogue attribute the editor picks, including names
 * not enumerated in `ATTR_NAMES` — which must resolve to `undefined`, not throw.
 */
export function getTiAttributeValue(
  product: CmsProduct | undefined,
  attrName: string,
): string | undefined {
  const attr = getAttrFromProductByName(attrName, product);
  if (!attr) return undefined;

  if (attr.__typename === 'ComponentAttributesAttrNumber') {
    if (!attr.value || String(attr.value).trim() === '') return undefined;
    return formatAttributeItemValue(attr, true, true).trim() || undefined;
  }

  if (attr.__typename === 'ComponentAttributesAttrPlainText') {
    const value = attr.value?.trim();
    return value ? value : undefined;
  }

  const formatted = formatAttributeItemValue(attr).trim();
  return formatted || undefined;
}
